/**
 * Plantillas de correo para expedientes AR.
 *
 * ── Por qué existen ─────────────────────────────────────────────────────────
 * Hoy el agente de IA **redacta el correo** (`sendEmailWithoutTemplate(email,
 * args.subject, args.body)` en `services/aiAgent/actionExecutor.js`), y por eso
 * los expedientes AR están excluidos del envío automático en
 * `AUTO_EMAIL_EXCLUDED_TYPES`. Dejar que un modelo escriba texto libre a una
 * cartera entera desde el dominio del cliente no es aceptable. Las plantillas
 * son lo que convierte «que escriba» en «que elija», y por tanto lo que permite
 * abrir esa exclusión.
 *
 * ── Qué NO son ──────────────────────────────────────────────────────────────
 * No son las plantillas dinámicas de SendGrid que usa recobro. Aquellas viven
 * en SendGrid y ClaimUp solo guarda un array POSICIONAL de identificadores
 * (`user.emailTemplates.default`): el contenido no se puede ni leer desde el
 * producto y el significado lo lleva la posición. Aquí el texto vive en el
 * producto, versionado y editable. Es la decisión **E7** del plan.
 *
 * ── Diferencia con las de WhatsApp ──────────────────────────────────────────
 * Las de Kapso las **aprueba Meta**, por cuenta de negocio y una a una, y por
 * eso su modelo carga con estado de aprobación, huella del texto aprobado y
 * reintentos. En correo no hay nadie que apruebe nada: el mismo texto sale hoy.
 * Lo que sí se copia de allí, tal cual, es el orden por cliente.
 */

/**
 * Claves de la librería estándar.
 *
 * **Son las mismas que las de WhatsApp a propósito** (ver
 * `kapsoTemplates.js`). «Primer contacto» es el mismo peldaño de la misma
 * cadencia, se mande por el canal que se mande, y compartir clave es lo que
 * permitirá comparar los dos recorridos —o alternarlos— sin traducir nada.
 * Viven en colecciones distintas, así que no colisionan.
 */
const AR_EMAIL_TEMPLATE_KEYS = {
  PRIMER_CONTACTO: 'ar_primer_contacto',
  SEGUNDO_AVISO: 'ar_segundo_aviso',
  TERCER_AVISO: 'ar_tercer_aviso',
};

const {
  EXPEDIENT_STATUSES,
  ALL_EXPEDIENT_STATUSES,
  FINALIZED_STATUSES,
} = require('./expedient');

/**
 * Estados a los que escribe la secuencia **por defecto**, al dar de alta un
 * cliente. A partir de ahí cada empresa configura los suyos desde la pantalla:
 * viven en su fila de `companyemailcadences`, no aquí.
 *
 * Es una **lista blanca**: un estado nuevo no empieza a recibir correos solo por
 * existir. La lista negra de recobro (`EXCLUDED_STATES` en
 * `interactionDecisor.js`) hace lo contrario, y por eso cada estado que se
 * inventa hay que acordarse de excluirlo.
 *
 * El default es **más estrecho que `ELIGIBLE_PROCESSING_STATUSES`** de
 * `services/aiAgent/eligibility/common.js`, que además admite `Hot`: un deudor
 * «caliente» está negociando, y mandarle un aviso automático es la misma
 * imprudencia que seguir escribiendo a quien ya contestó. Pero es un **default**,
 * no una prohibición: quien quiera incluirlo, puede.
 */
const DEFAULT_AR_EMAIL_STATUSES = [
  EXPEDIENT_STATUSES.ON_GOING,
  EXPEDIENT_STATUSES.COLD,
  EXPEDIENT_STATUSES.EMAIL_OPENED,
];

/**
 * Los estados que la pantalla ofrece, y los únicos que el backend acepta.
 *
 * **Los finalizados quedan fuera y no son configurables.** Escribirle a alguien
 * cuyo expediente está en `Cobrado` —ya pagó—, `Cancelled` o `Fallido` no es una
 * preferencia que cada cliente pueda tener: es un defecto, y lo lee una persona
 * real. `Bounced` es el caso más claro: significa que su dirección ya falló, así
 * que insistir solo daña la reputación del dominio.
 *
 * Van ordenados por lo que de verdad ocurre en una cartera de AR; detrás, los
 * judiciales, que existen pero rara vez se cruzan con este canal.
 */
const AR_RELEVANT_ORDER = [
  EXPEDIENT_STATUSES.ON_GOING,
  EXPEDIENT_STATUSES.EMAIL_OPENED,
  EXPEDIENT_STATUSES.HOT,
  EXPEDIENT_STATUSES.COLD,
  EXPEDIENT_STATUSES.DISPUTE,
  EXPEDIENT_STATUSES.PLAN_DE_PAGOS,
  EXPEDIENT_STATUSES.COBRO_PARCIAL_EXTRAJUDICIAL,
];

const SELECTABLE_AR_EMAIL_STATUSES = [
  ...AR_RELEVANT_ORDER,
  ...ALL_EXPEDIENT_STATUSES.filter(
    (e) => !FINALIZED_STATUSES.includes(e) && !AR_RELEVANT_ORDER.includes(e)
  ),
];

/**
 * Cadencia: cuándo sale la secuencia automática.
 *
 * Los valores por defecto **son los de recobro hoy**, que están escritos a mano
 * en tres `cron.schedule` de `services/scripts/cronJobs.js`: lunes, miércoles y
 * jueves. Se copian aquí para que un cliente nuevo empiece con lo que ya se
 * sabe que funciona, no porque el número tenga nada de sagrado.
 */
const DIAS_SEMANA = [
  [1, 'Lunes'],
  [2, 'Martes'],
  [3, 'Miércoles'],
  [4, 'Jueves'],
  [5, 'Viernes'],
  [6, 'Sábado'],
  [7, 'Domingo'],
];

const DEFAULT_CADENCE_DAYS = [1, 3, 4];
const DEFAULT_CADENCE_HOUR = 10;
const DEFAULT_CADENCE_TIMEZONE = 'Europe/Madrid';

/**
 * Cuántas veces se puede repetir una misma plantilla antes de subir de peldaño.
 *
 * En recobro esto se expresa **repitiendo el id en el array**: cuatro entradas
 * iguales seguidas son cuatro envíos de esa plantilla. Aquí es un número, que
 * es lo mismo pero se puede leer.
 */
const MAX_REPEATS_PER_TEMPLATE = 10;

/** Tope de peldaños que ofrece la pantalla. Más de esto no lo ha pedido nadie. */
const MAX_ESCALATION_ORDER = 5;

/**
 * Cada cuántos días se repite el ÚLTIMO peldaño cuando la escalera se acaba.
 *
 * Decisión de Edgar del 22/10/2026: al acabar la escalera **no se deja de
 * insistir**. Pero no con la cadencia normal —que son hasta cuatro correos por
 * semana al mismo deudor—, sino con su propio intervalo, y por una razón
 * concreta: el correo sale del subdominio del cliente y **esa reputación la
 * comparte toda su cartera**. Insistir a diario a quien no abre ni contesta es
 * el patrón que manda a spam también el primer contacto de los deudores nuevos,
 * que es el que más vale.
 *
 * `0` apaga la insistencia: la secuencia se cierra como `finished` al agotar la
 * escalera, que es lo que hacía antes de esta decisión.
 */
const DEFAULT_REPEAT_LAST_EVERY_DAYS = 14;
const MAX_REPEAT_LAST_EVERY_DAYS = 365;

const MAX_SUBJECT_LENGTH = 150;
const MAX_BODY_LENGTH = 4000;

/**
 * Las variables que sabemos rellenar, con de dónde sale cada una.
 *
 * Es una **lista cerrada**: al guardar una plantilla se valida contra ella. Sin
 * eso, alguien escribe `{{urlPago}}` cuando el campo es `payment_url` y el
 * correo sale con un hueco — y nos enteramos por el deudor, que es la peor
 * forma de enterarse.
 */
const EMAIL_TEMPLATE_VARIABLES = {
  customer_name: 'Nombre del deudor',
  company_name: 'Marca comercial del expediente, o nombre de la empresa',
  debt_title:
    'Concepto de la deuda: el título del asiento, tal como se ve en la sección «Deuda» del expediente. Con varios asientos salen todos, separados por comas',
  amount: 'Importe de la deuda, formateado con su divisa — ya trae el símbolo: NO escribas «€» delante',
  amount_number: 'El mismo importe, sin símbolo de divisa (1.234,50), para escribirlo tú con otra moneda o formato',
  debt_detail:
    'El detalle de la deuda, una línea por asiento: «concepto · importe · fecha». Es lo que evita escribir importes y fechas a mano',
  debt_date:
    'La fecha de los asientos de deuda (13/06/2026). Con varios asientos salen todas, separadas por comas',
  payment_url:
    'El enlace de pago de CADA expediente, no uno fijo. OJO: no todos lo tienen — hay clientes enteros sin ninguno — y a esos deudores el correo les llega SIN la línea donde la pongas, así que escríbela de forma que se pueda caer sola',
};

const KNOWN_VARIABLE_NAMES = Object.keys(EMAIL_TEMPLATE_VARIABLES);

/**
 * Variables que **no paran el envío** cuando el expediente no las tiene: en vez
 * de dejar el correo sin salir, se quita **la línea entera** donde estaban.
 *
 * Solo `payment_url`, y por un número que **no es la media**. A 27/10/2026 el
 * enlace de pago propio lo tienen 12.181 de 32.613 expedientes, pero reparte por
 * cliente y no de forma uniforme: Cleverea, TaxDown, Access Finance, Mi Préstamo
 * y Axi al 100%, y **Tattoox (772), Anticimex (681) y Saldados (399) al 0%**. Las
 * tres plantillas del catálogo lo usan, así que con el bloqueo de antes a un
 * cliente al 0% no le salía **ningún** correo, en silencio (motivo
 * `incomplete_template`). Anticimex es uno de los que van a AR.
 *
 * Se quita la línea y no solo el hueco porque la frase que lo rodea deja de
 * tener sentido: «puede hacerlo aquí:» sin enlace es peor que no decir nada.
 *
 * El resto de variables **siguen parando el envío**, y eso no se toca: un correo
 * que dice «su factura ⟨debt_title⟩» lo lee una persona.
 */
const OPTIONAL_EMAIL_VARIABLES = ['payment_url'];

/**
 * Borradores de la librería estándar. **Textos provisionales**: sirven para
 * construir y probar el mecanismo. Los definitivos son decisión de producto.
 *
 * El `escalation_order` de cada uno define el recorrido POR DEFECTO: al añadir
 * la plantilla a un cliente se lleva su peldaño, y desde ahí se cambia si ese
 * cliente necesita otro orden.
 */
const AR_EMAIL_TEMPLATE_DRAFTS = [
  {
    key: AR_EMAIL_TEMPLATE_KEYS.PRIMER_CONTACTO,
    escalation_order: 1,
    description: 'Primer contacto sobre una factura pendiente.',
    subject: 'Pendiente de pago: {{debt_title}} · {{company_name}}',
    body_text:
      'Hola {{customer_name}},\n\n' +
      'Le escribimos de {{company_name}}. Nos consta pendiente de pago ' +
      '{{debt_title}}, por importe de {{amount}}.\n\n' +
      'Si ya lo ha abonado o hay algo que revisar, responda a este correo y lo vemos. ' +
      'Si prefiere resolverlo ahora, puede hacerlo aquí: {{payment_url}}\n\n' +
      'Gracias.',
    variables: [
      { name: 'customer_name', example: 'Jessica' },
      { name: 'company_name', example: 'Acme Industries' },
      { name: 'debt_title', example: 'FE23077567' },
      { name: 'amount', example: '300,00 €' },
      { name: 'payment_url', example: 'https://pago.acme.com/F-2026-0451' },
    ],
  },
  {
    key: AR_EMAIL_TEMPLATE_KEYS.SEGUNDO_AVISO,
    escalation_order: 2,
    description: 'Segundo intento, una semana después del primero.',
    subject: 'Recordatorio: {{debt_title}} sigue pendiente',
    body_text:
      'Hola {{customer_name}},\n\n' +
      'Volvemos a escribirle desde {{company_name}} por ' +
      '{{debt_title}}, de {{amount}}, que sigue pendiente.\n\n' +
      'Si necesita el detalle, o quiere plantear una forma de pago distinta, ' +
      'responda a este correo y lo gestionamos.\n\n' +
      'Puede pagarla directamente aquí: {{payment_url}}',
    variables: [
      { name: 'customer_name', example: 'Jessica' },
      { name: 'company_name', example: 'Acme Industries' },
      { name: 'debt_title', example: 'FE23077567' },
      { name: 'amount', example: '300,00 €' },
      { name: 'payment_url', example: 'https://pago.acme.com/F-2026-0451' },
    ],
  },
  {
    key: AR_EMAIL_TEMPLATE_KEYS.TERCER_AVISO,
    escalation_order: 3,
    description: 'Último intento antes de cerrar el circuito automático.',
    subject: 'Último aviso · {{debt_title}}',
    body_text:
      'Hola {{customer_name}},\n\n' +
      'Este es nuestro último aviso desde {{company_name}} sobre ' +
      '{{debt_title}}, por importe de {{amount}}.\n\n' +
      'Si no recibimos respuesta, el expediente seguirá su curso. Puede ' +
      'contestarnos a este correo en cualquier momento, o abonarla aquí: {{payment_url}}',
    variables: [
      { name: 'customer_name', example: 'Jessica' },
      { name: 'company_name', example: 'Acme Industries' },
      { name: 'debt_title', example: 'FE23077567' },
      { name: 'amount', example: '300,00 €' },
      { name: 'payment_url', example: 'https://pago.acme.com/F-2026-0451' },
    ],
  },
];

/**
 * Lo que el editor puede marcar en un trozo de texto.
 *
 * Es una **lista cerrada**, y es lo que sustituye a sanear HTML en el servidor:
 * el cuerpo no se guarda como HTML sino como estructura, asi que basta con
 * validar que los atributos estan aqui. Anadir uno obliga a ensenar al
 * renderizador a pintarlo — si no, se guarda y se pierde en silencio.
 */
const EMAIL_OPS_SIZES = ['small', 'large', 'huge'];
const EMAIL_OPS_ALIGNS = ['center', 'right', 'justify'];
const EMAIL_OPS_LISTS = ['bullet', 'ordered'];

/**
 * Tope de operaciones por plantilla.
 *
 * No es por el tamano del correo —de eso ya se ocupa MAX_BODY_LENGTH— sino
 * porque cada trozo con formato distinto es una operacion mas: un texto corto
 * con la mitad de las palabras en negrita puede traer cientos. Es un tope de
 * cordura contra un documento pegado desde Word.
 */
const MAX_OPS = 500;

/**
 * Botones por plantilla.
 *
 * Tres es un tope de criterio, no tecnico: un correo con cuatro botones no
 * tiene llamada a la accion, tiene un menu, y el deudor no pulsa ninguno.
 */
const MAX_BUTTONS = 3;
const MAX_BUTTON_LABEL = 40;

module.exports = {
  AR_EMAIL_TEMPLATE_KEYS,
  AR_EMAIL_TEMPLATE_DRAFTS,
  EMAIL_TEMPLATE_VARIABLES,
  KNOWN_VARIABLE_NAMES,
  OPTIONAL_EMAIL_VARIABLES,
  DEFAULT_AR_EMAIL_STATUSES,
  SELECTABLE_AR_EMAIL_STATUSES,
  MAX_ESCALATION_ORDER,
  DEFAULT_REPEAT_LAST_EVERY_DAYS,
  MAX_REPEAT_LAST_EVERY_DAYS,
  MAX_REPEATS_PER_TEMPLATE,
  DIAS_SEMANA,
  DEFAULT_CADENCE_DAYS,
  DEFAULT_CADENCE_HOUR,
  DEFAULT_CADENCE_TIMEZONE,
  MAX_SUBJECT_LENGTH,
  MAX_BODY_LENGTH,
  EMAIL_OPS_SIZES,
  EMAIL_OPS_ALIGNS,
  EMAIL_OPS_LISTS,
  MAX_OPS,
  MAX_BUTTONS,
  MAX_BUTTON_LABEL,
};
